import React, { useMemo } from 'react';
import { Button, List, Space, Tag, Typography } from 'antd';
import {
  RightOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ExceptionOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import type { QualityDashboardActionSummary } from '@/services/qualityDashboard.service';

const { Text } = Typography;

type Severity = 'critical' | 'attention' | 'info';

interface AttentionItem {
  id: string;
  severity: Severity;
  title: string;
  reason: string;
  count: number;
  action_label: string;
  section: string;
  owner_hint?: string;
}

const severityColor: Record<Severity, string> = {
  critical: 'red',
  attention: 'orange',
  info: 'blue',
};

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const QmsAttentionBrief: React.FC<{
  summary: QualityDashboardActionSummary;
}> = ({ summary }) => {
  const items = useMemo(() => {
    const out: AttentionItem[] = [];
    const critical = summary.critical_ncr_count ?? 0;
    const openNcr = summary.open_ncr_count ?? 0;
    const ncr30 = summary.open_ncr_aging?.['30+'] ?? 0;
    const overdueCapa = summary.overdue_capa_count ?? 0;
    const capa30 = summary.overdue_capa_aging?.['30+'] ?? 0;
    const overdueInsp = summary.overdue_inspection_count ?? 0;
    const failed = summary.failed_stage_count ?? 0;
    const woGates = summary.wo_gates_waiting_count ?? 0;
    const approvals = summary.approvals_waiting_count ?? 0;
    const dueSoon = summary.plan_items_due_soon_count ?? 0;

    if (critical > 0) {
      out.push({
        id: 'critical-ncr',
        severity: 'critical',
        title: 'Critical open NCRs',
        reason: 'Critical nonconformances need immediate containment and disposition.',
        count: critical,
        action_label: 'NCR queue',
        section: 'ncr-capa-follow-up',
        owner_hint: 'Quality',
      });
    }
    if (openNcr > 0 && critical === 0) {
      out.push({
        id: 'open-ncr',
        severity: ncr30 > 0 ? 'critical' : 'attention',
        title: 'Open NCRs',
        reason:
          ncr30 > 0
            ? `${ncr30} aged 30+ days — escalate disposition and CAPA linkage.`
            : 'Open NCRs awaiting investigation or closure.',
        count: openNcr,
        action_label: 'NCR queue',
        section: 'ncr-capa-follow-up',
        owner_hint: 'Quality',
      });
    } else if (openNcr > 0 && ncr30 > 0) {
      out.push({
        id: 'aged-ncr',
        severity: 'critical',
        title: 'Aged open NCRs (30+)',
        reason: 'Long-aged NCRs — escalate disposition and CAPA linkage.',
        count: ncr30,
        action_label: 'NCR queue',
        section: 'ncr-capa-follow-up',
        owner_hint: 'Quality',
      });
    }
    if (overdueCapa > 0) {
      out.push({
        id: 'overdue-capa',
        severity: 'critical',
        title: 'Overdue CAPAs',
        reason:
          capa30 > 0
            ? `${capa30} aged 30+ days past target — extend formally or close evidence.`
            : 'Corrective actions past target completion — chase owners.',
        count: overdueCapa,
        action_label: 'CAPA queue',
        section: 'ncr-capa-follow-up',
        owner_hint: 'Quality / Process owners',
      });
    }
    if (overdueInsp > 0) {
      out.push({
        id: 'overdue-inspection',
        severity: 'critical',
        title: 'Overdue inspections',
        reason: 'Scheduled inspections past due — complete or reschedule.',
        count: overdueInsp,
        action_label: 'Inspection queue',
        section: 'ncr-capa-follow-up',
        owner_hint: 'Inspectors',
      });
    }
    if (failed > 0) {
      out.push({
        id: 'failed-stage',
        severity: 'attention',
        title: 'Failed stage inspections',
        reason: 'Stage gates failed — rework / re-inspect before WO can proceed.',
        count: failed,
        action_label: 'WO gates',
        section: 'wo-inspection-gates',
        owner_hint: 'Production / Quality',
      });
    }
    if (woGates > 0) {
      out.push({
        id: 'wo-gates',
        severity: 'attention',
        title: 'WO inspection gates waiting',
        reason: 'IIR, stage, or FIR requests blocking work-order progress.',
        count: woGates,
        action_label: 'Clear gates',
        section: 'wo-inspection-gates',
        owner_hint: 'Quality',
      });
    }
    if (approvals > 0) {
      out.push({
        id: 'approvals',
        severity: 'attention',
        title: 'Pending quality approvals',
        reason: 'IRN, FIR report, or quality plan awaiting approval.',
        count: approvals,
        action_label: 'Clear approvals',
        section: 'pending-approvals',
        owner_hint: 'Approvers',
      });
    }
    if (dueSoon > 0) {
      out.push({
        id: 'plan-due-soon',
        severity: 'info',
        title: 'Plan items due in 7 days',
        reason: 'Quality plan inspections scheduled soon — confirm readiness.',
        count: dueSoon,
        action_label: 'Plan items',
        section: 'plan-items',
        owner_hint: 'Quality planning',
      });
    }
    return out;
  }, [summary]);

  const highlights = useMemo(() => {
    const tags: string[] = [];
    if ((summary.critical_ncr_count ?? 0) > 0) {
      tags.push(`${summary.critical_ncr_count} critical NCR`);
    }
    if ((summary.open_ncr_count ?? 0) > 0) {
      tags.push(`${summary.open_ncr_count} open NCR`);
    }
    if ((summary.overdue_capa_count ?? 0) > 0) {
      tags.push(`${summary.overdue_capa_count} overdue CAPA`);
    }
    if ((summary.overdue_inspection_count ?? 0) > 0) {
      tags.push(`${summary.overdue_inspection_count} overdue insp.`);
    }
    if ((summary.wo_gates_waiting_count ?? 0) > 0) {
      tags.push(`${summary.wo_gates_waiting_count} WO gates`);
    }
    if ((summary.approvals_waiting_count ?? 0) > 0) {
      tags.push(`${summary.approvals_waiting_count} approvals`);
    }
    return tags;
  }, [summary]);

  const iconFor = (id: string) => {
    if (id.includes('ncr') || id === 'failed-stage') return <WarningOutlined />;
    if (id.includes('capa') || id === 'approvals') return <ExceptionOutlined />;
    if (id.includes('inspection') || id === 'plan-due-soon') return <ClockCircleOutlined />;
    if (id === 'wo-gates') return <FileTextOutlined />;
    return <ThunderboltOutlined />;
  };

  return (
    <ControlTowerSectionCard
      zone="attention"
      sectionId="attention-brief"
      title={
        <Space>
          <ThunderboltOutlined />
          Attention Brief
        </Space>
      }
    >
      {highlights.length > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          {highlights.map((h) => (
            <Tag key={h} color="geekblue">
              {h}
            </Tag>
          ))}
        </Space>
      )}
      {!items.length ? (
        <EmptyQueueState message="No urgent NCR, CAPA, inspection, or gate items right now." />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(a) => (
            <List.Item
              actions={[
                <Button
                  key="go"
                  type="link"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => scrollTo(a.section)}
                >
                  {a.action_label}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={iconFor(a.id)}
                title={
                  <Space wrap>
                    <Tag color={severityColor[a.severity]}>{a.severity}</Tag>
                    <Text strong>{a.title}</Text>
                    <Tag>{a.count}</Tag>
                  </Space>
                }
                description={
                  <Text type="secondary">
                    Why: {a.reason}
                    {a.owner_hint ? ` · ${a.owner_hint}` : ''}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
      {(summary.inspection_pass_rate_30d != null || (summary.open_capa_count ?? 0) > 0) && (
        <Space wrap style={{ marginTop: 8 }}>
          {summary.inspection_pass_rate_30d != null ? (
            <Tag icon={<CheckCircleOutlined />} color="cyan">
              Pass rate 30d: {summary.inspection_pass_rate_30d}%
            </Tag>
          ) : null}
          {(summary.open_capa_count ?? 0) > 0 ? (
            <Tag color="purple">Open CAPAs: {summary.open_capa_count}</Tag>
          ) : null}
        </Space>
      )}
    </ControlTowerSectionCard>
  );
};

export default QmsAttentionBrief;
